import { useEffect, useState } from "react";
import { fetchSentiment, refreshSentiment } from "../lib/sentimentApi.js";

// ─── Expat-profiel config ─────────────────────────────────────
const EXPAT_LABELS = {
  families: { icon: "👨‍👩‍👧", label: "Gezinnen",       bg: "#e1f5ee", color: "#0f6e56" },
  surfers:  { icon: "🏄", label: "Surfers",         bg: "#eaf3de", color: "#3b6d11" },
  nomads:   { icon: "💻", label: "Digital nomads",  bg: "#e6f1fb", color: "#185fa5" },
  retirees: { icon: "🏖", label: "Gepensioneerden", bg: "#faeeda", color: "#854f0b" },
};

// ─── Topic config ─────────────────────────────────────────────
const TOPIC_LABELS = {
  housing:     "🏡 Wonen",
  schools:     "🏫 Scholen",
  climate:     "🌡 Klimaat",
  community:   "🤝 Community",
  bureaucracy: "📋 Bureaucratie",
};

// ─── Subcomponents ───────────────────────────────────────────
function SentimentBar({ value, label }) {
  const color = value > 20 ? "#3d6e48" : value > -20 ? "#d4a851" : "#b85c4d";
  const pct   = (value + 100) / 2;
  return (
    <div className="sent-bar">
      <div className="sent-bar-label">{label}</div>
      <div className="sent-bar-track">
        <div className="sent-bar-zero" />
        <div className="sent-bar-fill"
             style={{ width: `${Math.abs(value) / 2}%`, background: color,
                      left: value > 0 ? "50%" : `${pct}%` }} />
      </div>
      <div className="sent-bar-value" style={{ color }}>{value > 0 ? "+" : ""}{value}</div>
    </div>
  );
}

function ExpatProfile({ profiles }) {
  if (!profiles?.length) return null;
  return (
    <div className="sent-profiles">
      <span className="sent-section-label">Expat-profiel</span>
      <div className="sent-profile-chips">
        {profiles.map(p => {
          const cfg = EXPAT_LABELS[p];
          if (!cfg) return null;
          return (
            <span key={p} className="sent-profile-badge"
                  style={{ background: cfg.bg, color: cfg.color }}>
              {cfg.icon} {cfg.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function TopicFilter({ counts, active, onChange }) {
  const total = Object.values(counts || {}).reduce((a, b) => a + b, 0);
  if (total === 0) return null;
  return (
    <div className="sent-topics">
      <span className="sent-section-label">Onderwerp</span>
      <div className="sent-topic-chips">
        <button className={`sent-topic-chip${!active ? " active" : ""}`}
                onClick={() => onChange(null)} type="button">
          Alles
        </button>
        {Object.entries(counts || {}).filter(([, n]) => n > 0).map(([id, n]) => (
          <button key={id}
                  className={`sent-topic-chip${active === id ? " active" : ""}`}
                  onClick={() => onChange(active === id ? null : id)}
                  type="button">
            {TOPIC_LABELS[id] || id}
            <span className="sent-topic-count">{n}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ProsCons({ pros, cons, showOriginal }) {
  if (!pros?.length && !cons?.length) return null;
  const p = pros  || [];
  const c = cons  || [];
  if (p.length === 0 && c.length === 0) return null;
  return (
    <div className="sent-proscons">
      {p.length > 0 && (
        <div className="sent-proscons-col pros">
          <div className="sent-proscons-title">✓ Pluspunten</div>
          {p.map((text, i) => (
            <div key={i} className="sent-proscons-item">
              <span className="sent-proscons-dot p" />
              <span>{text}</span>
            </div>
          ))}
        </div>
      )}
      {c.length > 0 && (
        <div className="sent-proscons-col cons">
          <div className="sent-proscons-title">⚠ Minpunten</div>
          {c.map((text, i) => (
            <div key={i} className="sent-proscons-item">
              <span className="sent-proscons-dot c" />
              <span>{text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CostCard({ costs }) {
  if (!costs) return null;
  return (
    <div className="sent-costs">
      <span className="sent-section-label">
        Kosten (Numbeo · {costs.city})
      </span>
      <div className="sent-costs-grid">
        {costs.rent_1br && (
          <div className="sent-cost-item">
            <div className="sent-cost-val">€{costs.rent_1br.toLocaleString("nl-NL")}</div>
            <div className="sent-cost-lbl">Huur 1kk/mnd</div>
          </div>
        )}
        {costs.rent_3br && (
          <div className="sent-cost-item">
            <div className="sent-cost-val">€{costs.rent_3br.toLocaleString("nl-NL")}</div>
            <div className="sent-cost-lbl">Huur 3kk/mnd</div>
          </div>
        )}
        {costs.coffee && (
          <div className="sent-cost-item">
            <div className="sent-cost-val">€{costs.coffee.toFixed(2)}</div>
            <div className="sent-cost-lbl">Koffie (café)</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────
export function SentimentCard({ concelho }) {
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [expanded,     setExpanded]     = useState(false);
  const [activeTopic,  setActiveTopic]  = useState(null);

  useEffect(() => {
    if (!concelho?.slug) return;
    setLoading(true);
    setExpanded(false);
    setActiveTopic(null);
    fetchSentiment(concelho.slug).then(d => { setData(d); setLoading(false); });
  }, [concelho?.slug]);

  const doRefresh = async () => {
    setRefreshing(true);
    await refreshSentiment(concelho.slug);
    const fresh = await fetchSentiment(concelho.slug);
    setData(fresh);
    setRefreshing(false);
    setExpanded(false);
    setActiveTopic(null);
  };

  if (loading) return (
    <section className="sent-card">
      <h3>💬 Sociale omgeving</h3>
      <p className="sent-loading">Laden…</p>
    </section>
  );

  if (!data) return (
    <section className="sent-card">
      <h3>💬 Sociale omgeving</h3>
      <p className="sent-empty">Nog geen data. Eerste keer ophalen duurt ~1 min.</p>
      <button className="sent-refresh-btn" onClick={doRefresh} disabled={refreshing}>
        {refreshing ? "Bezig…" : "Haal social media op"}
      </button>
    </section>
  );

  const en     = data.enriched || {};
  const allPosts = data.sample_reddit || [];
  const posts  = activeTopic
    ? allPosts.filter(p => p.topic === activeTopic)
    : allPosts;
  const visible = expanded ? posts : posts.slice(0, 3);

  const prosToShow = showOriginal ? en.pros : (en.pros_nl?.length ? en.pros_nl : en.pros);
  const consToShow = showOriginal ? en.cons : (en.cons_nl?.length ? en.cons_nl : en.cons);

  return (
    <section className="sent-card">
      {/* Header */}
      <div className="sent-header">
        <h3>💬 Sociale omgeving</h3>
        <button className="sent-refresh-btn-small" onClick={doRefresh} disabled={refreshing}>
          {refreshing ? "Bezig…" : "↻ Vernieuw"}
        </button>
      </div>
      <p className="sent-sub">
        Reddit · {(data.queries_used || []).length} zoekopdrachten · laatste 90 dagen
      </p>

      {/* Stats */}
      <div className="sent-stats">
        <div className="sent-stat">
          <span className="sent-stat-num">{data.reddit_count}</span>
          <span className="sent-stat-label">Posts</span>
        </div>
        <div className="sent-stat">
          <span className="sent-stat-num"
                style={{ color: data.reddit_sentiment > 20 ? "#1d9e75" : data.reddit_sentiment < -20 ? "#b85c4d" : "#d4a851" }}>
            {data.reddit_sentiment > 0 ? "+" : ""}{data.reddit_sentiment}
          </span>
          <span className="sent-stat-label">Toon</span>
        </div>
        <div className="sent-stat">
          <span className="sent-stat-num">{(data.queries_used || []).length}</span>
          <span className="sent-stat-label">Queries</span>
        </div>
      </div>

      {/* Expat profiel */}
      <ExpatProfile profiles={en.expat_profiles} />

      {/* Topic filter */}
      <TopicFilter counts={en.topic_counts} active={activeTopic} onChange={setActiveTopic} />

      {/* Pros / Cons */}
      <ProsCons pros={prosToShow} cons={consToShow} showOriginal={showOriginal} />

      {/* Sentiment balken */}
      {data.reddit_count > 0 && <SentimentBar value={data.reddit_sentiment} label="Reddit-toon" />}
      {data.news_count   > 0 && <SentimentBar value={data.news_sentiment}   label="Nieuws-toon" />}

      {/* Keywords */}
      {data.top_reddit_keywords?.length > 0 && (
        <div className="sent-keywords">
          <span className="sent-keywords-label">Meest besproken:</span>
          {data.top_reddit_keywords.map(k => (
            <span key={k.word} className="sent-tag" title={`EN: ${k.word} (${k.count}×)`}>
              {k.word_nl || k.word}
              <small className="sent-tag-count">{k.count}</small>
            </span>
          ))}
        </div>
      )}

      {/* Posts */}
      {visible.length > 0 && (
        <div className="sent-sample">
          <div className="sent-sample-head">
            <span className="sent-sample-label">
              Interessantste posts
              {activeTopic && <span className="sent-topic-active-label"> · {TOPIC_LABELS[activeTopic]}</span>}
            </span>
            <button type="button" className="sent-toggle-orig" onClick={() => setShowOriginal(s => !s)}>
              {showOriginal ? "🇳🇱 NL" : "🇬🇧 EN"}
            </button>
          </div>

          {visible.map((p, i) => (
            <a key={i} href={p.url} target="_blank" rel="noopener" className="sent-sample-row">
              <span className="sent-sample-rank">{i + 1}</span>
              <span className="sent-sample-body">
                <span className="sent-sample-title">
                  {showOriginal ? p.title : (p.title_nl || p.title)}
                </span>
                <span className="sent-sample-meta">
                  <span className="sent-sample-sub">r/{p.subreddit}</span>
                  <span className="sent-sample-upvotes">↑{p.score}</span>
                  {p.query && (
                    <span className="sent-sample-query" title={`Gevonden via: "${p.query}"`}>
                      🔍 {p.query}
                    </span>
                  )}
                  {p.topic && (
                    <span className="sent-sample-topic">{TOPIC_LABELS[p.topic]}</span>
                  )}
                </span>
              </span>
            </a>
          ))}

          {posts.length > 3 && (
            <button type="button" className="sent-expand-btn" onClick={() => setExpanded(v => !v)}>
              {expanded ? "▴ Minder tonen" : `▾ Nog ${posts.length - 3} post${posts.length - 3 !== 1 ? "s" : ""} tonen`}
            </button>
          )}
        </div>
      )}

      {posts.length === 0 && activeTopic && (
        <p className="sent-empty">Geen posts gevonden voor dit onderwerp.</p>
      )}

      {/* Numbeo kosten */}
      <CostCard costs={en.costs} />

      {/* Footer */}
      <div className="sent-foot">
        <small>Bijgewerkt: {new Date(data.fetched_at).toLocaleDateString("nl-NL")}</small>
      </div>
    </section>
  );
}
